/*
 * Copyright (c) 2024 Amazon.com, Inc. All Rights Reserved.
 */
import "./ModelPage.css";
import { ModelTable } from "./ModelTable";
import { ModelEntry } from "../../model/ModelEntry";
import { Region } from "../../model/Region";
import { DetectionSystem } from "../../model/DetectionSystem";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ModelForm } from "./ModelForm";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { Navbar, NavbarPages } from "../navbar/Navbar";
import { StageConfig } from "../../config/StageConfig";
import sentryFetch from "@amzn/sentry-fetch";
import { RequestType } from "../../model/RequestType";
import { DateFilterValues, NumberFilterValues } from "../../model/FilterValues";
import { getTimeFromDate } from "../../config/DateUtils";
import { Snackbar } from "@mui/material";
import { isJSONResponse } from "../../config/Requests";

/**
 * @author stcatai Mar 22, 2024
 */
export function ModelPage() {
  const PAGE_SIZES = [10, 20, 30, 40, 50];

  const [stageConfig, setStageConfig] = useState<StageConfig>();
  const [showCreate, setShowCreate] = useState<boolean>(false);

  useEffect(() => {
    if (showCreate) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => document.body.classList.remove('modal-open');
  }, [showCreate]);
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [responseMessage, setResponseMessage] = useState<string>("");

  const [page, setPage] = useState<number>(1);
  const [valuesPerPage, setValuesPerPage] = useState<number>(10);
  const [region, setRegion] = useState<Region>();
  const [detectionSystem, setDetectionSystem] = useState<DetectionSystem>();
  const [publishable, setPublishable] = useState<boolean>();
  const [label, setLabel] = useState<string>();
  const [modelName, setModelName] = useState<string>();
  const [lastCommitDescription, setLastCommitDescription] = useState<string>();
  const [lastCommitAuthor, setLastCommitAuthor] = useState<string>();
  const [ttl, setTtl] = useState<NumberFilterValues>({});
  const [commitDate, setCommitDate] = useState<DateFilterValues>({});
  const [models, setModels] = useState<ModelEntry[]>([]);

  const navigate = useNavigate();

  const createButtonRef = useRef(null);
  const createModalRef = useRef(null);

  useEffect(() => {
    navigate("/");
  }, [navigate]);

  useEffect(() => {
    fetch("../stageConfig.json")
      .then(res => res.json())
      .then(stageConfig => setStageConfig(stageConfig));
  }, []);

  useEffect(() => {
    if (!stageConfig) return;

    sentryFetch(`${stageConfig.url}/modelsManager?requestType=${RequestType.SEARCH_MODELS}`+
      `&region=${region || ""}&detectionSystem=${detectionSystem || ""}&publishable=${publishable || ""}`+
      `&ttlGreaterThan=${ttl.greater || ""}&ttlLessThan=${ttl.less || ""}&ttlEquals=${ttl.equal || ""}`+
      `&lastCommitTimestampFrom=${getTimeFromDate(commitDate.fromDate) || ""}&lastCommitTimestampTo=${getTimeFromDate(commitDate.toDate) || ""}`+
      `&label=${label || ""}&modelName=${modelName || ""}&lastCommitDescription=${lastCommitDescription || ""}`+
      `&lastCommitAuthor=${lastCommitAuthor || ""}&page=${page}&valuesPerPage=${valuesPerPage}`, {
      method: "GET",
      credentials: "include",
      sentryOptions: {
        followMidwayStepUp: true
      }
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        } else {
          return response.text().then(response => {
            if (isJSONResponse(response)) {
              throw new Error(JSON.parse(response).message);
            } else {
              throw new Error(response);
            }
          });
        }
      })
      .then(models => setModels(models))
      .catch(error => {
        console.error(error.message);
        setResponseMessage(error.message);
        setShowMessage(true);
      });
  }, [stageConfig, region, detectionSystem, publishable, label,
    ttl, commitDate, modelName, lastCommitDescription, lastCommitAuthor, page, valuesPerPage]);

  useOutsideClick([createButtonRef, createModalRef], () => setShowCreate(false));

  return <>
    {stageConfig && <div className="model-page">
      <Navbar selectedPage={NavbarPages.ModelEntries} stage={stageConfig.stage}/>
      <div className="table-container">
        <div className="buttons">
          <button className="add-button" onClick={() => setShowCreate(!showCreate)} ref={createButtonRef}>+</button>
          <div className="page-buttons">
            <label htmlFor="perPage">Page size:</label>
            <select id="perPage" defaultValue={10} onChange={(e) => setValuesPerPage(parseInt(e.target.value))}>
              {PAGE_SIZES.map((pageSize, idx) =>
                <option value={pageSize} key={idx}>{pageSize}</option>
              )}
            </select>
            <button onClick={() => setPage(page-1)} disabled={page === 1}>{"<"}</button>
            <span>Page {page}</span>
            <button onClick={() => setPage(page+1)} disabled={models && models.length < valuesPerPage}>{">"}</button>
          </div>
        </div>

        {showCreate && <div className="modal">
          <div className="modal-content" ref={createModalRef}>
            <ModelForm requestType={RequestType.ADD_MODEL} setShowModelForm={setShowCreate}/>
          </div>
        </div>
        }
        <ModelTable models={models} setRegionFetch={setRegion} setDetectionSystemFetch={setDetectionSystem}
                    setPublishableFetch={setPublishable} setLabelFetch={setLabel} setModelNameFetch={setModelName}
                    setLastCommitDescriptionFetch={setLastCommitDescription} setLastCommitAuthorFetch={setLastCommitAuthor}
                    setTtlFetch={setTtl} setCommitDateFetch={setCommitDate}/>
        <Snackbar
          open={showMessage}
          autoHideDuration={5000}
          onClose={() => setShowMessage(false)}
          message={responseMessage}
          anchorOrigin={{vertical: "bottom", horizontal: "center"}}
        />
      </div>
    </div>}
  </>
}